/**
 * AppWideTriggersCard — Phase 2 admin trigger for forcing password
 * or MFA rotation across an entire role bucket in one shot.
 *
 * Flow:
 *   1. Admin picks role (or "all") + which trigger (password / MFA).
 *   2. Component fetches a live preview count from
 *      /api/admin/security/app-wide-preview so the admin sees the
 *      blast radius BEFORE confirming.
 *   3. Confirm opens a password+MFA dialog. Backend verifies the
 *      admin's credentials inline (same brute-force protection as
 *      /auth/login) before flipping the flags. Calling admin is
 *      ALWAYS excluded server-side so they can't lock themselves
 *      out mid-execution.
 *
 * The user-side rotation modals (already shipped pre-iter151) pick
 * up the new flags and prompt each user on their next session — no
 * additional user-facing UI needed here.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { AlertTriangle, KeyRound, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const ROLE_OPTIONS = [
  { value: '', label: 'All roles' },
  { value: 'affiliate', label: 'Affiliates' },
  { value: 'lead', label: 'Leads' },
  { value: 'supervisor', label: 'Supervisors' },
  { value: 'special', label: 'Special users' },
  { value: 'admin', label: 'Admins (except you)' },
];

export default function AppWideTriggersCard() {
  const { api, user } = useAuth();
  const requiresMfa = !!user?.totp_enabled;
  const [scopeRole, setScopeRole] = useState('');
  const [preview, setPreview] = useState({ password_count: null, mfa_count: null });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmKind, setConfirmKind] = useState(null); // 'password' | 'mfa' | null
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const params = scopeRole ? { role: scopeRole } : {};
      const res = await api.get('/admin/security/app-wide-preview', { params });
      setPreview({
        password_count: res.data?.password_count ?? 0,
        mfa_count: res.data?.mfa_count ?? 0,
      });
    } catch (e) {
      setPreview({ password_count: null, mfa_count: null });
    } finally {
      setPreviewLoading(false);
    }
  }, [api, scopeRole]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const openConfirm = (kind) => {
    setError(null);
    setPassword('');
    setTotpCode('');
    setReason('');
    setConfirmKind(kind);
  };

  const closeConfirm = () => {
    if (submitting) return;
    setConfirmKind(null);
    setError(null);
  };

  const submit = async () => {
    if (!password) {
      setError('Enter your admin password.');
      return;
    }
    if (requiresMfa && (!totpCode || totpCode.length < 6)) {
      setError('Enter your 6-digit authenticator code.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const path = confirmKind === 'password'
        ? '/admin/security/force-password-rotation-all'
        : '/admin/security/force-mfa-rotation-all';
      const body = {
        password,
        totp_code: requiresMfa ? totpCode : null,
        role: scopeRole || null,
        reason: reason || null,
      };
      const res = await api.post(path, body);
      const n = res.data?.affected_count ?? 0;
      toast.success(`Flagged ${n} user${n === 1 ? '' : 's'} for ${confirmKind === 'password' ? 'password' : 'MFA'} rotation`);
      setConfirmKind(null);
      // refresh preview — the just-flagged users are now excluded.
      loadPreview();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Trigger failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card data-testid="app-wide-triggers-card">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">App-wide Triggers</p>
            <AlertTriangle className="w-4 h-4 text-amber-600" />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Flag every user in the selected bucket to rotate their password or MFA on next session. You're always excluded.
          </p>

          <div>
            <Label className="text-xs">Scope</Label>
            <select
              value={scopeRole}
              onChange={(e) => setScopeRole(e.target.value)}
              className="w-full h-9 text-sm rounded border border-input bg-background px-2"
              data-testid="app-wide-scope"
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-2 pt-1">
            <Button
              variant="outline"
              onClick={() => openConfirm('password')}
              disabled={previewLoading || preview.password_count === 0}
              data-testid="app-wide-force-password-btn"
              className="justify-between"
            >
              <span className="flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5" />
                Force password rotation
              </span>
              <span className="text-xs text-muted-foreground" data-testid="app-wide-password-count">
                {previewLoading ? '…' : `${preview.password_count ?? '?'} pending`}
              </span>
            </Button>
            <Button
              variant="outline"
              onClick={() => openConfirm('mfa')}
              disabled={previewLoading || preview.mfa_count === 0}
              data-testid="app-wide-force-mfa-btn"
              className="justify-between"
            >
              <span className="flex items-center gap-2">
                <Shield className="w-3.5 h-3.5" />
                Force MFA rotation
              </span>
              <span className="text-xs text-muted-foreground" data-testid="app-wide-mfa-count">
                {previewLoading ? '…' : `${preview.mfa_count ?? '?'} pending`}
              </span>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!confirmKind} onOpenChange={(v) => { if (!v) closeConfirm(); }}>
        <DialogContent className="bg-white dark:bg-[#1f1f1f] rounded-2xl max-w-md" data-testid="app-wide-confirm-dialog">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Confirm app-wide {confirmKind === 'password' ? 'password' : 'MFA'} rotation
          </DialogTitle>
          <DialogDescription className="text-xs">
            This will flag {confirmKind === 'password' ? preview.password_count : preview.mfa_count} {scopeRole || 'cross-role'} user{(confirmKind === 'password' ? preview.password_count : preview.mfa_count) === 1 ? '' : 's'}. Re-enter your credentials to confirm.
          </DialogDescription>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Admin password</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                autoComplete="current-password"
                data-testid="app-wide-password-input"
              />
            </div>
            {requiresMfa && (
              <div>
                <Label className="text-xs">Authenticator code</Label>
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  data-testid="app-wide-totp-input"
                />
              </div>
            )}
            <div>
              <Label className="text-xs">Reason (optional, audit log)</Label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. quarterly rotation, security incident"
                data-testid="app-wide-reason-input"
              />
            </div>
            {error && (
              <p className="text-xs text-red-600" data-testid="app-wide-error">{error}</p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={closeConfirm} disabled={submitting} data-testid="app-wide-cancel">
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-[#586D52] hover:bg-[#475a43]"
                data-testid="app-wide-confirm"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
